// The log, newest first. The lines of one turn sit over one marker: an arrow
// that points up at them, the turn number, and a rule. Every line starts with
// an icon. A line a spell wrote has the spell's icon in a circle of its
// polarity color, and the name is the tooltip of the circle. Any other line
// has a muted icon for its type. A click on a line puts the board on the step that
// wrote it. Lines from after the step on the board are dim.

import { LOG_DISPLAY_LIMIT, LOG_ENTRY_HINT, LOG_TURN_LABEL } from '../constants';
import type { GameState, LogEntry } from '../game/state';
import { el } from '../lib/dom';
import { svg } from '../lib/svg';
import { spellById } from '../spells/library';
import { gameStore } from '../state/game';
import { goToLogEntry, historyStore, reviewedStep } from '../state/history';
import { shownLog } from '../state/rollShown';
import { stageStore } from '../state/stage';
import { createLogToneIcon } from './LogToneIcon';
import { createSpellIcon } from './SpellIcon';

const PLIES_IN_A_TURN = 2;
const ARROW_BOX = 24;
/** A solid triangle that points up, the shape of a step in the history controls. */
const ARROW_UP = 'M4 18 H20 L12 6 Z';

type RenderedEntry = { node: HTMLElement; turn: number };
type RenderedMarker = { node: HTMLElement; firstEntryId: number };

const turnOf = (entry: LogEntry): number => Math.floor(entry.ply / PLIES_IN_A_TURN) + 1;

const spellChip = (entry: LogEntry): HTMLElement | null =>
  entry.spell && entry.spellId
    ? el(
        'span',
        {
          className: spellById(entry.spellId).voiced
            ? 'chronicle__spell'
            : 'chronicle__spell chronicle__spell--spec',
          dataset: { tip: entry.spell },
          role: 'img',
          ariaLabel: entry.spell,
        },
        [createSpellIcon(entry.spellId, 'chronicle__glyph')],
      )
    : null;

const entryNode = (entry: LogEntry): HTMLElement =>
  el(
    'li',
    {
      className: `chronicle__entry chronicle__entry--${entry.tone}`,
      dataset: { tip: LOG_ENTRY_HINT },
      onclick: () => goToLogEntry(entry.id),
    },
    [
      spellChip(entry) ?? createLogToneIcon(entry.tone),
      el('span', { className: 'chronicle__text', textContent: entry.text }),
    ],
  );

const markerNode = (turn: number): HTMLElement =>
  el('li', { className: 'chronicle__turn' }, [
    svg(
      'svg',
      {
        class: 'chronicle__arrow',
        viewBox: `0 0 ${ARROW_BOX} ${ARROW_BOX}`,
        'aria-hidden': 'true',
      },
      [svg('path', { d: ARROW_UP })],
    ),
    el('span', { className: 'chronicle__turn-label', textContent: `${LOG_TURN_LABEL} ${turn}` }),
    el('span', { className: 'chronicle__rule' }),
  ]);

export const createLog = (): HTMLElement => {
  const list = el('ol', { className: 'log' });
  const entries = new Map<number, RenderedEntry>();
  const markers = new Map<number, RenderedMarker>();

  const removeStale = (visible: readonly LogEntry[]): void => {
    const visibleIds = new Set(visible.map((entry) => entry.id));
    for (const [id, entry] of entries) {
      if (visibleIds.has(id)) continue;
      entry.node.remove();
      entries.delete(id);
    }
    const turnsWithLines = new Set([...entries.values()].map((entry) => entry.turn));
    for (const [turn, marker] of markers) {
      if (turnsWithLines.has(turn)) continue;
      marker.node.remove();
      markers.delete(turn);
    }
  };

  const draw = (state: GameState): void => {
    // The lines a roll wrote wait until its die lands.
    const visible = shownLog(state).slice(-LOG_DISPLAY_LIMIT);
    removeStale(visible);
    for (const entry of visible) {
      if (entries.has(entry.id)) continue;
      const turn = turnOf(entry);
      if (!markers.has(turn)) {
        const marker = markerNode(turn);
        markers.set(turn, { node: marker, firstEntryId: entry.id });
        list.prepend(marker);
      }
      const node = entryNode(entry);
      entries.set(entry.id, { node, turn });
      list.prepend(node);
    }
    drawCursor();
  };

  const drawCursor = (): void => {
    const step = reviewedStep();
    const isLater = (id: number): boolean => step !== null && id >= step.state.nextLogId;
    for (const [id, entry] of entries) {
      entry.node.classList.toggle('chronicle__entry--later', isLater(id));
    }
    for (const marker of markers.values()) {
      marker.node.classList.toggle('chronicle__turn--later', isLater(marker.firstEntryId));
    }
  };

  draw(gameStore.get());
  gameStore.subscribe(draw);
  stageStore.subscribe(() => draw(gameStore.get()));
  historyStore.subscribe(drawCursor);
  return list;
};
